import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { Card } from "react-bootstrap";
import apiAxios from "../../../config/ApiAxios";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker, { registerLocale } from "react-datepicker";
import { es } from "date-fns/locale";
import { useQuery } from "../../../utils/useQuery";
import { useHistory } from "react-router-dom";
import { handleGraficosParams } from "../../../utils/handleGraficosParams";
import { DesdeButton, HastaButon } from "../DesdeYHastaButtons";
import { parseDate } from "../../../utils/dateFormatter";
import { useLocation } from "react-router-dom";
import { globalEndDate, globalStartDate } from "../../../config/reportesGlobales";

registerLocale("es", es);

const CreditosOtorgados = () => {
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);

  const generarCategorias = (startYear, startMonth, endYear, endMonth) => {
    const meses = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];

    let categories = [];

    startMonth -= 1;
    endMonth -= 1;

    for (let year = startYear; year <= endYear; year++) {
      let firstMonth = year === startYear ? startMonth : 0;
      let lastMonth = year === endYear ? endMonth : 11;

      for (let mes = firstMonth; mes <= lastMonth; mes++) {
        if (mes === firstMonth && year === startYear) {
          categories.push(`${meses[mes]} ${year}`);
        } else if (mes === lastMonth && year === endYear) {
          categories.push(`${meses[mes]} ${year}`);
        } else {
          categories.push(meses[mes]);
        }
      }
    }

    return categories;
  };

  const transformarDatos = (data, startYear, startMonth, endYear, endMonth) => {
    const totalMeses = (endYear - startYear) * 12 + (endMonth - startMonth + 1);
    const otorgadosData = Array(totalMeses).fill(0);
    const terminadosData = Array(totalMeses).fill(0);
    const activosData = Array(totalMeses).fill(0);

    const indexMes = (anio, mes) => {
      const yearDiff = anio - startYear;
      const monthDiff = mes - startMonth;
      return yearDiff * 12 + monthDiff;
    };

    data.otorgados.forEach((entry) => {
      if (
        entry.anio >= startYear &&
        entry.anio <= endYear &&
        (entry.anio > startYear || entry.mes >= startMonth) &&
        (entry.anio < endYear || entry.mes <= endMonth)
      ) {
        const index = indexMes(entry.anio, entry.mes);
        otorgadosData[index] = entry.cantidad;
      }
    });

    data.terminados.forEach((entry) => {
      if (
        entry.anio >= startYear &&
        entry.anio <= endYear &&
        (entry.anio > startYear || entry.mes >= startMonth) &&
        (entry.anio < endYear || entry.mes <= endMonth)
      ) {
        const index = indexMes(entry.anio, entry.mes);
        terminadosData[index] = entry.cantidad;
      }
    });

    data.activos.forEach((entry) => {
      if (
        entry.anio >= startYear &&
        entry.anio <= endYear &&
        (entry.anio > startYear || entry.mes >= startMonth) &&
        (entry.anio < endYear || entry.mes <= endMonth)
      ) {
        const index = indexMes(entry.anio, entry.mes);
        activosData[index] = entry.cantidad;
      }
    });

    return {
      otorgados: otorgadosData,
      terminados: terminadosData,
      activos: activosData,
    };
  };

  const [startDate, setStartDate] = useState(new Date(globalStartDate));
  const [endDate, setEndDate] = useState(globalEndDate);

  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fechaInicio = searchParams.get("fecha-inicio");
    const fechaFinal = searchParams.get("fecha-final");

    if (fechaInicio) {
      const parsedStartDate = parseDate(fechaInicio);
      setStartDate(parsedStartDate);
    }

    if (fechaFinal) {
      const parsedEndDate = parseDate(fechaFinal);
      setEndDate(parsedEndDate);
    }
  }, [location.search]);

  const startDateYear = startDate.getFullYear();
  const startDateMonth = String(startDate.getMonth() + 1).padStart(2, "0");
  const startDateDay = String(startDate.getDate()).padStart(2, "0");

  const endDateYear = endDate.getFullYear();
  const endDateMonth = String(endDate.getMonth() + 1).padStart(2, "0");
  const endDateDay = String(endDate.getDate()).padStart(2, "0");

  useEffect(() => {
    const obtenerCreditos = async () => {
      try {
        const apiResponse = await apiAxios.get(
          `/reportes/global/creditos/por-estado?fecha_desde=${startDateYear}-${startDateMonth}-${startDateDay}&fecha_hasta=${endDateYear}-${endDateMonth}-${endDateDay}`
        );
        if (apiResponse?.status === 200) {
          const categorias = generarCategorias(
            startDateYear,
            startDateMonth,
            endDateYear,
            endDateMonth
          );
          setCategories(categorias);

          const datosTransformados = transformarDatos(
            apiResponse?.data,
            startDateYear,
            startDateMonth,
            endDateYear,
            endDateMonth
          );

          setSeries([
            {
              name: "Otorgados",
              data: datosTransformados.otorgados,
            },
            {
              name: "Terminados",
              data: datosTransformados.terminados,
            },
            {
              name: "Activos",
              data: datosTransformados.activos,
            },
          ]);
        }
      } catch (e) {
        console.log(e);
      }
    };

    obtenerCreditos();
  }, [
    endDate,
    endDateDay,
    endDateMonth,
    endDateYear,
    startDate,
    startDateDay,
    startDateMonth,
    startDateYear,
  ]);

  const options = {
    chart: {
      type: "bar",
      height: 350,
      stacked: true,
      width: "100%",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      orientation: "vertical",
      position: "center center",
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: categories,
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val;
        },
      },
    },
  };

  const query = useQuery();
  const history = useHistory();

  const ocultarGrafico = query.get("ocultarGrafico");

  const ocultarGraficoArray = ocultarGrafico
    ? ocultarGrafico.replace(/[[\]]/g, "").split(",").map(Number)
    : [];

  return (
    <div
      style={{
        display: ocultarGraficoArray.includes(3) ? "none" : "block",
      }}
    >
      <div className="px-2 py-2 border border-bottom-0 d-flex align-items-center gap-1">
        <DatePicker
          locale={"es"}
          selected={startDate}
          onChange={(date) => setStartDate(date)}
          selectsStart
          startDate={startDate}
          endDate={endDate}
          dateFormat={"dd/MM/yyyy"}
          customInput={<DesdeButton className="" />}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          minDate={"01/01/2010"}
          maxDate={"01/01/2040"}
        />
        <i class="bi bi-arrow-right"></i>
        <DatePicker
          locale={"es"}
          selectsEnd
          selected={endDate}
          onChange={(date) => setEndDate(date)}
          startDate={startDate}
          endDate={endDate}
          minDate={startDate}
          dateFormat={"dd/MM/yyyy"}
          customInput={<HastaButon className="me-3" />}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          maxDate={"01/01/2040"}
        />
      </div>
      <Card style={{ width: "100%" }}>
        <Card.Body>
          <Card.Title>
            Créditos Otorgados
            <button
              title="Ocultar"
              className="btn btn-light ms-2"
              onClick={() =>
                handleGraficosParams(3, ocultarGraficoArray, history)
              }
            >
              <i class="bi bi-eye"></i>
            </button>
            <a
                title="Este gráfico de columnas muestra, para cada mes/año, la cantidad de créditos distribuidos en tres categorías: Otorgados, Terminados y Activos.
                Las cantidades de 'Terminados' y 'Activos' reflejan el estado actual (respecto a hoy) de los créditos otorgados en el mes(barra) indicado , no cuando fueron terminados, o cuando se mantuvieron activos"
                className="btn btn-light ms-2"
                disabled
              >
                <i class="bi bi-info-circle"></i>
              </a>
          </Card.Title>
          <Chart options={options} series={series} type="bar" height={450} />
        </Card.Body>
      </Card>
    </div>
  );
};

export default CreditosOtorgados;
